import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Button, Chip, Avatar, Alert, LinearProgress, Divider, Tooltip,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import TimelineIcon from '@mui/icons-material/Timeline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useMe } from '../context/MeContext';
import PatientFormDialog from '../components/PatientFormDialog';
import DeletePatientDialog from '../components/DeletePatientDialog';
import { useSnackbar } from '../components/Snackbar';
import { usePatient, formatAge } from '../hooks/usePatient';
import { getPatientHistory } from '../api/patientApi';
import { getCategory } from '../api/categoryApi';
import { getActiveAssessment } from '../api/saasApi';
import { getDomainProgress, getDomainProgressbyName } from '../components/CategoryLevels';
import { PageHeader, SectionCard, EmptyState, DetailSkeleton } from '../components/ui';
import { level as levelTokens } from '../theme/tokens';

const initials = (name = '') => {
  const parts = String(name).trim().split(' ').filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

/**
 * The record for one child — the page the product was missing. Previously a
 * patient was a table row with two buttons and no place that showed the whole
 * picture: where they are across the domains, what's been done, what's next.
 */
const PatientDetail = () => {
  const navigate = useNavigate();
  const { patientId, patient, setPatient, loading, error } = usePatient();
  const { can, billing, isExempt, isPlatformAdmin } = useMe();
  const gated = !!billing && !isExempt && !isPlatformAdmin;
  // While a paid assessment is in progress the report is only reachable through
  // the "Finish & view report" flow (which ends the assessment), so hide it here.
  const [assessmentOpen, setAssessmentOpen] = useState(false);

  const [categories, setCategories] = useState([]);
  const [history, setHistory] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  // Vqura patients live in the external directory; only this clinic's own
  // records are ours to correct.
  const canEdit = can('patient:update') && patient?.source === 'local';
  const canDelete = can('patient:delete') && patient?.source === 'local';
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (!patientId) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const [cats, hist] = await Promise.all([getCategory(), getPatientHistory(patientId)]);
        if (cancelled) return;
        setCategories(cats.data?.data || []);
        setHistory(hist.data?.data || []);
      } catch (e) {
        // A patient with no assessments yet is the normal case, not an error.
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [patientId]);

  useEffect(() => {
    if (!patientId || !gated) { setAssessmentOpen(false); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await getActiveAssessment(patientId);
        if (!cancelled) setAssessmentOpen(res.data?.data?.status === 'Paid');
      } catch (e) {
        // Fail closed: if we can't tell, don't offer the report link.
        if (!cancelled) setAssessmentOpen(true);
      }
    })();
    return () => { cancelled = true; };
  }, [patientId, gated]);

  /**
   * Latest submission per domain, joined onto the domain catalogue so domains
   * never assessed still appear — the point is to see the gaps.
   */
  const domainState = useMemo(() => {
    const latest = [...history].sort(
      (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)
    )[0];

    const achievedByDomain = {};
    (latest?.domains || []).forEach((d) => {
      achievedByDomain[d.domain] = d.skillsAchieved;
    });

    return categories.map((cat) => {
      const achieved = achievedByDomain[cat.name] ?? achievedByDomain[cat.code];
      const assessed = achieved !== undefined;

      // CategoryLevels only defines thresholds for 8 domains (SEN/BEH absent,
      // and it uses RC/EC where the DB uses RL/EL), so a null result is expected
      // for some domains — show the raw count rather than inventing a level.
      const progress = assessed
        ? getDomainProgressbyName(cat.name, achieved) || getDomainProgress(cat.code, achieved)
        : null;

      return {
        code: cat.code,
        name: cat.name,
        assessed,
        achieved: achieved ?? 0,
        level: progress?.level ?? null,
        percentage: progress ? Number(progress.percentage) : null,
      };
    });
  }, [categories, history]);

  const lastAssessed = useMemo(() => {
    if (history.length === 0) return null;
    return [...history].sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))[0];
  }, [history]);

  const assessedCount = domainState.filter((d) => d.assessed).length;

  if (loading) return <DetailSkeleton />;

  if (error) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Alert severity="warning">{error}</Alert>
        <Button onClick={() => navigate('/patient')}>Back to patients</Button>
      </Stack>
    );
  }

  return (
    <>
      <PageHeader
        title={patient?.name || 'Patient'}
        subtitle={
          [
            patient?.dateOfBirth && `${formatAge(patient.dateOfBirth)} old`,
            patient?.mobileNo,
            `ID ${patientId}`,
          ].filter(Boolean).join('  ·  ')
        }
        actions={
          <>
            <Button
              variant="contained"
              startIcon={<AssignmentIcon />}
              onClick={() => navigate(`/patient/${patientId}/assess`)}
            >
              {assessedCount > 0 ? 'Continue assessment' : 'Start assessment'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => navigate(`/patient/${patientId}/history`)}
            >
              History
            </Button>
            {canEdit && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditOpen(true)}
              >
                Edit details
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setDeleteOpen(true)}
              >
                Delete
              </Button>
            )}
          </>
        }
      />

      <DeletePatientDialog
        patient={deleteOpen && patient ? { id: Number(patientId), name: patient.name, mobileNo: patient.mobileNo } : null}
        onClose={() => setDeleteOpen(false)}
        onDeleted={(p) => {
          setDeleteOpen(false);
          showSnackbar(`${p.name || 'Patient'} (ID ${p.id}) deleted.`, 'success');
          navigate('/patient', { replace: true });
        }}
      />

      <PatientFormDialog
        open={editOpen}
        patient={patient}
        onClose={() => setEditOpen(false)}
        onSaved={(saved) => setPatient((prev) => ({ ...prev, ...saved }))}
      />

      <Stack spacing={3}>
        {/* ---- Summary strip ------------------------------------------------ */}
        <SectionCard>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'grey.100', color: 'text.secondary', fontWeight: 700 }}>
              {initials(patient?.name)}
            </Avatar>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
            <Box>
              <Typography variant="caption" color="text.secondary">Domains assessed</Typography>
              <Typography variant="h3">{assessedCount} of {categories.length || 10}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Assessments recorded</Typography>
              <Typography variant="h3">{history.length}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Last assessed</Typography>
              <Typography variant="h3">
                {lastAssessed ? new Date(lastAssessed.submittedAt).toLocaleDateString() : '—'}
              </Typography>
            </Box>
          </Stack>
        </SectionCard>

        {/* ---- Per-domain levels -------------------------------------------- */}
        <SectionCard
          title="Developmental profile"
          subtitle="Current level in each domain, from the most recent assessment"
          action={
            assessmentOpen ? (
              <Chip size="small" color="info" label="Assessment in progress" />
            ) : (
              <Button size="small" startIcon={<DescriptionIcon />} onClick={() => navigate(`/patient/${patientId}/report`)}>
                Progress report
              </Button>
            )
          }
        >
          {dataLoading ? (
            <LinearProgress />
          ) : categories.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Domain list unavailable.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
              {domainState.map((d) => {
                const tone = d.level ? levelTokens[d.level] : null;
                return (
                  // Display only: scoring starts from "Start assessment" (which runs
                  // the payment step), never by clicking a domain here.
                  <Box
                    key={d.code}
                    sx={{
                      p: 2, borderRadius: 1,
                      border: '1px solid', borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} mb={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>{d.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{d.code}</Typography>
                      </Box>
                      {d.assessed ? (
                        d.level ? (
                          <Tooltip title={`${d.achieved} skills achieved`}>
                            <Chip
                              size="small"
                              label={`L${d.level}`}
                              sx={{ bgcolor: tone.light, color: tone.main, border: '1px solid', borderColor: tone.main }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip size="small" label={`${d.achieved} skills`} sx={{ bgcolor: 'grey.100' }} />
                        )
                      ) : (
                        <Chip size="small" variant="outlined" label="Not started" />
                      )}
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={d.assessed ? (d.percentage ?? 100) : 0}
                      sx={{
                        height: 6, borderRadius: 3, bgcolor: 'grey.100',
                        '& .MuiLinearProgress-bar': { bgcolor: tone ? tone.main : 'grey.400', borderRadius: 3 },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </SectionCard>

        {/* ---- History timeline ---------------------------------------------
             A boxed card per entry turned a long history into a wall of
             outlines. One rule between rows separates them without competing
             with the domain chips, which are the thing worth reading here. */}
        <SectionCard title="Assessment history" subtitle="Each entry is one reassessment">
          {history.length === 0 ? (
            <EmptyState
              variant="inline"
              icon={<TimelineIcon />}
              title="No assessments yet"
              body="Start an assessment to begin tracking this child's development over time."
              action={
                <Button variant="contained" onClick={() => navigate(`/patient/${patientId}/assess`)}>
                  Start assessment
                </Button>
              }
            />
          ) : (
            <Stack divider={<Divider />}>
              {[...history]
                .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
                .map((h) => (
                  <Stack
                    key={h.submissionNumber}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{
                      py: 1.5,
                      // The card already provides the outer padding.
                      '&:first-of-type': { pt: 0 },
                      '&:last-of-type': { pb: 0 },
                    }}
                  >
                    <Box sx={{ minWidth: 110 }}>
                      <Typography variant="subtitle2">
                        {new Date(h.submittedAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Assessment {h.submissionNumber}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flexGrow: 1 }}>
                      {(h.domains || []).map((d) => (
                        <Chip key={d.domain} size="small" label={`${d.domain}: ${d.skillsAchieved}`} sx={{ bgcolor: 'grey.100' }} />
                      ))}
                    </Stack>
                    {h.createdBy && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
                        {h.createdBy}
                      </Typography>
                    )}
                  </Stack>
                ))}
            </Stack>
          )}
        </SectionCard>
      </Stack>
    </>
  );
};

export default PatientDetail;
